import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { PageLoader, EmptyState } from "@/components/Loading";
import { StatusBadge, categoryLabel } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  ArrowRight, CalendarDays, Clock, MapPin, Users, User, GraduationCap, Trash2, BookOpen
} from "lucide-react";
import { toast } from "sonner";

const DEFAULT_IMG = "https://images.unsplash.com/photo-1663162550938-60f70fab5d31?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHwzfHx1bml2ZXJzaXR5JTIwY2FtcHVzJTIwbW9kZXJuJTIwc3R1ZGVudHN8ZW58MHx8fHwxNzg2MjgxODA4fDA&ixlib=rb-4.1.0&q=85";

export default function ProgramDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [p, setP] = useState(null);
  const [myEnrollment, setMyEnrollment] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const r = await api.get(`/programs/${id}`);
    setP(r.data);
    if (user.role === "student") {
      const e = await api.get("/enrollments");
      setMyEnrollment(e.data.find((x) => x.program_id === id) || null);
    }
  };
  useEffect(() => { load().catch(() => setP(false)); }, [id]);

  const enroll = async () => {
    setBusy(true);
    try { await api.post("/enrollments", { program_id: id }); toast.success("تم إرسال طلب التسجيل"); await load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const cancel = async () => {
    setBusy(true);
    try { await api.delete(`/enrollments/${myEnrollment.id}`); toast.success("تم إلغاء التسجيل"); await load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const remove = async () => {
    if (!window.confirm("هل أنت متأكد من حذف البرنامج؟")) return;
    try { await api.delete(`/programs/${id}`); toast.success("تم الحذف"); nav("/app/programs"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (p === null) return <PageLoader />;
  if (p === false) return <EmptyState icon={BookOpen} title="البرنامج غير موجود" />;

  return (
    <div className="animate-fade-in">
      <button onClick={() => nav("/app/programs")} className="mb-4 flex items-center gap-1 text-sm text-slate-500 hover:text-navy">
        <ArrowRight className="h-4 w-4" /> رجوع للبرامج
      </button>

      <div className="overflow-hidden rounded-2xl border border-slate-100 bg-white soft-shadow">
        <div className="relative h-56 w-full">
          <img src={p.image || DEFAULT_IMG} alt={p.name} className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-navy/70 to-transparent" />
          <div className="absolute bottom-5 start-6 text-white">
            <div className="mb-2"><StatusBadge status={p.status} /></div>
            <h1 className="font-heading text-3xl font-extrabold">{p.name}</h1>
          </div>
        </div>
        <div className="p-6">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Info icon={CalendarDays} label="الفترة" value={`${p.start_date || "—"} → ${p.end_date || "—"}`} />
            <Info icon={Clock} label="الوقت" value={`${p.start_time || "—"} - ${p.end_time || "—"}`} />
            <Info icon={MapPin} label="المكان" value={p.location || "—"} />
            <Info icon={Users} label="الفئة" value={categoryLabel[p.category]} />
            <Info icon={GraduationCap} label="الساعات" value={`${p.total_hours} ساعة`} />
            <Info icon={User} label="المسؤول" value={p.supervisor_name || "—"} />
            <Info icon={Users} label="المسجلون" value={`${p.enrolled_count}`} />
            <Info icon={GraduationCap} label="المحاضرات" value={`${p.lectures_count}`} />
          </div>

          {p.days?.length > 0 && (
            <div className="mt-5 flex flex-wrap gap-2">
              {p.days.map((d) => <span key={d} className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">{d}</span>)}
            </div>
          )}

          <div className="mt-5">
            <div className="mb-1 flex justify-between text-sm"><span className="text-slate-500">نسبة إكمال البرنامج</span><span className="font-semibold text-greenx">{p.completion}%</span></div>
            <Progress value={p.completion} className="h-2.5" />
          </div>

          <p className="mt-5 leading-[1.9] text-slate-600">{p.description}</p>

          {p.teacher_names?.length > 0 && (
            <div className="mt-5">
              <p className="mb-2 text-sm font-semibold text-navy">المدرسون والمحاضرون:</p>
              <div className="flex flex-wrap gap-2">{p.teacher_names.map((n, i) => <span key={i} className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-greenx">{n}</span>)}</div>
            </div>
          )}

          <div className="mt-6 flex flex-wrap gap-3">
            {user.role === "student" && (
              myEnrollment ? (
                <div className="flex items-center gap-3">
                  <span className="text-sm text-slate-500">حالة تسجيلك:</span>
                  <StatusBadge status={myEnrollment.status} />
                  {myEnrollment.status !== "approved" && (
                    <Button data-testid="cancel-enroll-btn" onClick={cancel} disabled={busy} variant="outline" className="rounded-xl border-red-200 text-red-500 hover:bg-red-50">إلغاء التسجيل</Button>
                  )}
                </div>
              ) : (
                <Button data-testid="enroll-btn" onClick={enroll} disabled={busy} className="rounded-xl bg-greenx px-8 hover:bg-greenx-light">التسجيل في البرنامج</Button>
              )
            )}
            {(user.role === "admin" || user.role === "supervisor") && (
              <Button data-testid="delete-program-btn" onClick={remove} variant="outline" className="rounded-xl border-red-200 text-red-500 hover:bg-red-50"><Trash2 className="me-2 h-4 w-4" /> حذف البرنامج</Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Info({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-3 rounded-xl border border-slate-100 p-3">
      <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-navy/5 text-navy"><Icon className="h-4.5 w-4.5" /></div>
      <div className="min-w-0">
        <p className="text-xs text-slate-400">{label}</p>
        <p className="truncate text-sm font-semibold text-navy">{value}</p>
      </div>
    </div>
  );
}
